import { AddCircle, Pets } from "@mui/icons-material";
import * as MuiMaterial from "@mui/material";
import React, { Fragment } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import usePetViewRoute from "../../features/pets/hooks/usePetViewRoute";
import { GlobalDataContext } from "../../contexts/GlobalData";
import { PETS_DROPDOWN, SPECIES_BOOKING_MODAL } from "../../enums/ApiPaths";
import { CREATE_PET } from "../../enums/PermissionTitles";
import * as keys from "../../enums/QueryKeys";
import { ADMIN, EMPLOYEE, USER } from "../../enums/Roles";
import { CHIP_MIN_WIDTH } from "../../enums/Styles";
import { useFetch } from "../../hooks/Fetching";
import useUser, { useGetUserId } from "../../hooks/useUser";
import NewPetBody from "./NewPetBody";

export default function PetSelector({
  pet,
  petsListProp = null,
  additionalPets = null,
  isNewUserSelected = false,
  queryKey = keys.PETS_DROP_DOWN, 
  url = PETS_DROPDOWN,
  noNewPets = false,
  shouldFilter = true,
  testId,
}) {
  const params = useParams();
  const [existing, setExisting] = React.useState(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  //A bit janky but it wokrs...
  //Can't get id directly from params becuase this component isn't located
  const {
    isPetView,
    petId: petViewPetId,
    ownerId: petViewOwnerId,
  } = usePetViewRoute();
  const petId = pet ? pet.id : isPetView ? petViewPetId : null;
  const userId = useGetUserId();
  //Make sure this is used, delete comment after implementation
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { data: dropDownPets, isLoading: loading } = useFetch(url, null, queryKey);

  function isDefined(array) {
    if (array != undefined && array != null) {
      return true;
    }
    return false;
  }

  const basePets = isDefined(petsListProp) ? petsListProp : dropDownPets;
  // Pets that aren't in the dropdown but still need to be selectable, e.g. the
  // pets of an appointment's user who has since moved to another clinic
  const extraPets = (additionalPets ?? []).filter(
    (extra) => !basePets?.some((pet) => Number(pet?.id) === Number(extra?.id)),
  );
  const allPets = extraPets.length ? [...(basePets ?? []), ...extraPets] : basePets;

  const fetch = useFetch(SPECIES_BOOKING_MODAL, null, keys.SPECIES_BOOKING_MODAL);
  const { t, i18n } = useTranslation();
  const [translatedSpecies, setTranslatedSpecies] = React.useState([]);
  const { currentClinic, roles } = useUser();
  const defaultUser = getDefaultUser();
  const selectedUser = watch("user");

  const getPetLabel = () => {
    if (roles.adminOrEmployee) {
      return selectedUser ? t("PickSelectedUsersPet") : t("PickPetFromClinic");
    }
    return t("SelectPet");
  };
  const petPickerLabel = getPetLabel();

  function getDefaultUser() {
    const userRegex = /^clinic\/user\/(\d+)$/;
    const userId = String(params["*"]).match(userRegex);

    if (userId) {
      return userId[1];
    } else if (petViewOwnerId) {
      return petViewOwnerId;
    } else {
      return null;
    }
  }

  function petRules() {
    if (!roles.adminOrEmployee) {
      return t("EmptyField");
    }
  }

  const getPetObjectByGivenId = (id) => {
    if (Number(id) == 0 || String(id) == "") {
      return "";
    }
    if (typeof id === "number" && allPets != null && allPets !== undefined) {
      const preSelectedPet = allPets.find((pet) => pet.id === id);
      return preSelectedPet;
    }
    return "";
  };

  const species = fetch?.data;

  React.useEffect(() => {
    if (Array.isArray(species)) {
      const currentLang = i18n.language;
      const translation = species.map((species) => {
        if (species?.translations) {
          return {
            id: species.id,
            title: species?.translations[currentLang] ?? species.title,
            breed: species?.breed,
          };
        } else {
          return {
            id: species.id,
            title: species.title,
            breed: species?.breed,
          };
        }
      });
      setTranslatedSpecies(translation);
    }
  }, [species, i18n.language]);

  function getTranslatedSpecies(pets) {
    return pets?.map((pet) => {
      var translated = translatedSpecies.find(
        (species) => species?.id == pet?.type?.id,
      );
      return {
        ...pet,
        type: translated,
      };
    });
  }

  function list(pets) {
    const filterDeceased = pets?.filter((pet) => !pet?.deceased);
    return getTranslatedSpecies(filterDeceased);
  }

  //This function returns a list of pets
  function getList() {
    let filteredPets = [];

    if (!shouldFilter) return allPets;

    if (roles.adminOrEmployee) {
      const clinicPets = allPets?.filter(
        (pet) => Number(pet?.clinic?.id) === Number(currentClinic?.id),
      );

      const user = watch("user");
      if (isNewUserSelected) {
        return new Array();
      }

      if (petId != null) {
        const petFromList = clinicPets.find(
          (pet) => Number(pet?.id) === Number(petId),
        );
        if (petFromList != null && petFromList != undefined) {
          //Pet belongs to clinic
          if (petFromList?.user == null || petFromList?.user == undefined) {
            list(clinicPets);
          }
        }
      }

      if (user != null && user != undefined && !isNewUserSelected) {
        // If the selected or pre-selected user has a role of user, filter pets belonging to the user
        if (user?.role === USER) {
          filteredPets = allPets?.filter((pet) => pet?.user?.id === user?.id);
        } else if (user?.role === ADMIN || user?.role === EMPLOYEE) {
          // If the selected or preselected user has a role of employee or admin, filter pets belonging to the clinic
          filteredPets = clinicPets;
        } else {
          // Fallback case for any other role (e.g., MASTER), return all pets
          filteredPets = allPets;
        }
      } else if (defaultUser) {
        filteredPets = allPets?.filter(
          (pet) => pet?.user?.id === Number(defaultUser),
        );
      } else {
        filteredPets = clinicPets;
      }
    } else {
      // For non-admin/non-employee users, return their pets
      filteredPets = allPets?.filter((pet) => Number(pet?.user?.id) === userId);
    }
    return list(filteredPets);
  }

  if (loading) {
    return <MuiMaterial.LinearProgress />;
  }

  function handleChip() {
    setExisting((prev) => !prev);
    setValue("pet", null);
    if (pet && !existing) {
      setValue("pet", pet);
    }
  }

  const petOptions = getList();

  if (noNewPets) {
    return (
      <Controller
        name="pet"
        control={control}
        defaultValue={getPetObjectByGivenId(petId)}
        rules={{ required: petRules() }}
        render={({ field: { value, onChange } }) => {
          const selectedPetId = Number(value?.id);
          const isValidValue = petOptions.find(
            (pet) => Number(pet.id) === selectedPetId,
          );

          return (
            <MuiMaterial.FormControl fullWidth error={!!errors.pet}>
              <MuiMaterial.InputLabel id="pet-select">
                {petPickerLabel}
              </MuiMaterial.InputLabel>
              <MuiMaterial.Select
                onChange={(e) => {
                  const selectedPetId = e.target.value;
                  const selectedPet = getPetObjectByGivenId(
                    Number(selectedPetId),
                  );
                  onChange(selectedPet);
                }}
                value={isValidValue ? selectedPetId : ""}
                label={petPickerLabel}
                SelectDisplayProps={{ ...(testId && { "data-testid": testId }) }}
              >
                <MuiMaterial.MenuItem value="">
                  <em>{t("DeselectPet")}</em>
                </MuiMaterial.MenuItem>
                {petOptions.map((pet) => (
                  <MuiMaterial.MenuItem value={pet.id} key={pet.id}>
                    {pet?.type?.title} {pet?.name}
                  </MuiMaterial.MenuItem>
                ))}
              </MuiMaterial.Select>
              <MuiMaterial.FormHelperText>
                {errors.pet?.message}
              </MuiMaterial.FormHelperText>
            </MuiMaterial.FormControl>
          );
        }}
      />
    );
  }

  return (
    <Fragment>
      <MuiMaterial.Stack direction={"row"} spacing={1} mb={3}>
        <MuiMaterial.Chip
          clickable
          sx={{
            minWidth: CHIP_MIN_WIDTH,
            display: "flex",
            justifyContent: "start",
          }}
          onClick={() => handleChip()}
          icon={<Pets />}
          label={t("Existing")}
          color={existing ? "primary" : "default"}
        />
        <MuiMaterial.Chip
          clickable
          sx={{
            minWidth: CHIP_MIN_WIDTH,
            display: "flex",
            justifyContent: "start",
          }}
          disabled={!isPermissionAllowed(CREATE_PET)}
          onClick={() => handleChip()}
          icon={<AddCircle />}
          label={t("NewPet")}
          color={!existing ? "primary" : "default"}
        />
      </MuiMaterial.Stack>
      <MuiMaterial.Stack>
        {!existing ? (
          <NewPetBody
            species={translatedSpecies}
            dateKey="pet.birthdate"
            descKey="pet.description"
            genderKey="pet.gender"
            nameKey="pet.name"
            speciesKey="pet.type"
            aggressionKey="pet.aggression"
            breedKey="pet.breed"
            bloodDonorKey="pet.bloodDonor"
            bloodTypeKey="pet.bloodType"
          />
        ) : (
          <Controller
            name="pet"
            control={control}
            defaultValue={getPetObjectByGivenId(petId)}
            rules={{ required: petRules() }}
            render={({ field: { value, onChange } }) => {
              const selectedPetId = Number(value?.id);
              const isValidValue = petOptions.find(
                (pet) => Number(pet.id) === selectedPetId,
              );

              return (
                <MuiMaterial.FormControl fullWidth error={!!errors.pet}>
                  <MuiMaterial.InputLabel id="pet-select">
                    {petPickerLabel}
                  </MuiMaterial.InputLabel>
                  <MuiMaterial.Select
                    onChange={(e) => {
                      const selectedPetId = e.target.value;
                      const selectedPet = getPetObjectByGivenId(
                        Number(selectedPetId),
                      );
                      onChange(selectedPet);
                    }}
                    value={isValidValue ? selectedPetId : ""}
                    label={petPickerLabel}
                    SelectDisplayProps={{ ...(testId && { "data-testid": testId }) }}
                  >
                    <MuiMaterial.MenuItem value="">
                      <em>{t("DeselectPet")}</em>
                    </MuiMaterial.MenuItem>
                    {petOptions.map((pet) => (
                      <MuiMaterial.MenuItem value={pet.id} key={pet.id}>
                        {pet?.type?.title} {pet?.name}
                      </MuiMaterial.MenuItem>
                    ))}
                  </MuiMaterial.Select>
                  <MuiMaterial.FormHelperText>
                    {errors.pet?.message}
                  </MuiMaterial.FormHelperText>
                </MuiMaterial.FormControl>
              );
            }}
          />
        )}
      </MuiMaterial.Stack>
    </Fragment>
  );
}
